/** Where the referral link was generated; the campaign value differs per surface. */
export type ReferralPlacement = 'Invite' | 'Checkout';

/**
 * Marketing's `{brand}_{vertical}` campaign prefix per brand id, and whether that brand's checkout has
 * a parent (`_P_`) campaign (only the driving brands sell parent purchases). Keyed by the upper-cased
 * brand id both apps already send xcc-api's mint (`brandConfig.id` in core-mkt, `xccEnv.brand` in
 * xcc-client), NOT Vision's `iterable.brandId`, which reports `ACE` for INS, MTG and HSP. Brands absent
 * here (HSP, AARP, QA, PA) have no campaign assigned, so their links are left exactly as GetAmbassador
 * returned them.
 */
const REFERRAL_CAMPAIGNS: Readonly<Partial<Record<string, { prefix: string; parent: boolean }>>> = {
  ACE: { prefix: 'A_D', parent: true },
  IDS: { prefix: 'I_D', parent: true },
  DEC: { prefix: 'D_D', parent: true },
  AA: { prefix: 'AA_RE', parent: false },
  MTG: { prefix: 'A_M', parent: false },
  INS: { prefix: 'A_I', parent: false },
};

/**
 * Builds the `utm_campaign` value for a referral share link: `{prefix}_{S|P}_{placement}_R`
 * (e.g. `A_D_P_Checkout_R`). Parent campaigns exist only for checkout on the driving brands; any other
 * parent request falls back to the student value. Returns null for a brand with no campaign so callers
 * leave the link untouched.
 */
export function referralCampaign(
  brand: string | null | undefined,
  placement: ReferralPlacement,
  isParent = false,
): string | null {
  const campaign = REFERRAL_CAMPAIGNS[(brand ?? '').toUpperCase()];
  if (!campaign) {
    return null;
  }
  const persona = isParent && placement === 'Checkout' && campaign.parent ? 'P' : 'S';
  return `${campaign.prefix}_${persona}_${placement}_R`;
}

/**
 * Sets `utm_medium=referral` and `utm_campaign` as the last two query params, in that order, replacing
 * any existing values so analytics never sees two `utm_campaign` keys (sync-marketing links already
 * carry GetAmbassador's campaign). Every other param keeps its original position and encoding. Returns
 * `url` unchanged for a null campaign or a non-http(s) or unparseable URL, so it never mangles a value
 * it does not understand.
 */
export function withReferralUtm(url: string, campaign: string | null): string {
  if (!campaign) {
    return url;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return url;
  }
  // Filter the raw query rather than going through `searchParams`, whose serialiser would re-encode
  // every other param (`%20` becomes `+`) on a link GetAmbassador parses on redirect.
  const kept = parsed.search
    .slice(1)
    .split('&')
    .filter((pair) => {
      const key = pair.split('=')[0];
      return pair !== '' && key !== 'utm_medium' && key !== 'utm_campaign';
    });
  parsed.search = [...kept, 'utm_medium=referral', `utm_campaign=${encodeURIComponent(campaign)}`].join('&');
  return parsed.toString();
}

/**
 * The one call sites use: tags `url` with the campaign for `brand` + `placement` (+ parent persona on
 * checkout), or returns it unchanged for a brand with no campaign.
 */
export function tagReferralLink(
  url: string,
  brand: string | null | undefined,
  placement: ReferralPlacement,
  isParent = false,
): string {
  return withReferralUtm(url, referralCampaign(brand, placement, isParent));
}
